import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkle, CheckCircle, XCircle, Copy, ArrowsClockwise, CalendarPlus, ShieldWarning, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { useRole } from "@/hooks/useRole";

const PLATFORMS = ["instagram", "facebook", "linkedin"];
const BEATS = [
  { key: "hook", label: "Hook" },
  { key: "agitate", label: "Agitate" },
  { key: "solution", label: "Solution" },
  { key: "proof", label: "Proof" },
  { key: "cta", label: "CTA" },
];

export default function Captions() {
  const api = useApi();
  const { canManageContent } = useRole();
  const [form, setForm] = useState({ topic: "", tone: "confident, warm, clinical", platform: "instagram", count: 3 });
  const [loading, setLoading] = useState(false);
  const [variations, setVariations] = useState([]);
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineTarget, setRefineTarget] = useState(null);
  const [refineText, setRefineText] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ platform: "instagram", scheduled_date: "", text: "" });
  const [sending, setSending] = useState(false);

  if (!canManageContent) {
    return <div className="text-[#7A9090] p-8" data-testid="captions-no-access">You don't have access to AI Captions.</div>;
  }

  const fullText = (v) => v.hashtags?.length ? `${v.text}\n\n${v.hashtags.map((h) => `#${h}`).join(" ")}` : v.text;

  const generate = async (payload) => {
    if (!payload.refine_text && !form.topic.trim()) { toast.error("Enter a topic first"); return; }
    setLoading(true);
    try {
      const res = await api.post("/captions/generate", { ...form, ...payload });
      setVariations(res.data.variations || []);
      if (!(res.data.variations || []).length) toast.error("No variations returned — try again");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setLoading(false); }
  };

  const copy = (v) => { navigator.clipboard.writeText(fullText(v)); toast.success("Caption copied"); };

  const openRefine = (v) => { setRefineTarget(v); setRefineText(""); setRefineOpen(true); };
  const doRefine = async () => {
    if (!refineText.trim()) { toast.error("Describe what to change"); return; }
    setRefineOpen(false);
    await generate({ refine_text: refineTarget.text, refine_instruction: refineText, count: 1 });
  };

  const openSend = (v) => { setSendForm({ platform: form.platform, scheduled_date: "", text: fullText(v) }); setSendOpen(true); };
  const doSend = async () => {
    if (!sendForm.text.trim() || !sendForm.scheduled_date) { toast.error("Add text and a date"); return; }
    setSending(true);
    try {
      await api.post("/posts", {
        draft_text: sendForm.text,
        platform: sendForm.platform,
        scheduled_date: new Date(sendForm.scheduled_date).toISOString(),
      });
      toast.success("Sent to calendar for approval");
      setSendOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create post"); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-8" data-testid="captions-container">
      <div>
        <h1 className="text-4xl font-bold text-[#3C4048] mb-2 tracking-tight" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>AI Captions</h1>
        <p className="text-[#7A9090]">Generate on-brand captions using the HASPC framework — Hook, Agitate, Solution, Proof, CTA.</p>
      </div>

      <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2">
            <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Topic</Label>
            <Input data-testid="caption-topic" value={form.topic} onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))} placeholder="e.g. desk posture & neck tension" className="border-[#e5e7eb] text-[#3C4048]" />
          </div>
          <div>
            <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Tone</Label>
            <Input data-testid="caption-tone" value={form.tone} onChange={(e) => setForm((f) => ({ ...f, tone: e.target.value }))} className="border-[#e5e7eb] text-[#3C4048]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Platform</Label>
              <select data-testid="caption-platform" value={form.platform} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))} className="w-full border border-[#e5e7eb] rounded-[10px] p-2 text-sm text-[#3C4048]">
                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Count</Label>
              <select data-testid="caption-count" value={form.count} onChange={(e) => setForm((f) => ({ ...f, count: parseInt(e.target.value) }))} className="w-full border border-[#e5e7eb] rounded-[10px] p-2 text-sm text-[#3C4048]">
                {[3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
        </div>
        <Button data-testid="generate-captions" onClick={() => generate({})} disabled={loading} className="mt-4 bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white gap-2 rounded-[12px]">
          <Sparkle size={18} weight="fill" /> {loading ? "Generating…" : "Generate captions"}
        </Button>
      </div>

      {loading && <p className="text-[#7A9090]" data-testid="captions-loading">Writing on-brand copy…</p>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {variations.map((v, i) => (
          <div key={i} data-testid={`caption-card-${i}`} className="bg-white border border-[#e5e7eb] rounded-[12px] p-5 flex flex-col">
            <p className="text-[#3C4048] text-sm whitespace-pre-line flex-1">{v.text}</p>

            {v.hashtags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {v.hashtags.map((h) => <span key={h} className="text-[#3A7A8C] text-xs bg-[#f0f7f8] px-2 py-0.5 rounded-full">#{h}</span>)}
              </div>
            )}

            {/* HASPC checklist */}
            <div className="mt-4 pt-4 border-t border-[#eef1f2]">
              <p className="text-[#7A9090] text-[11px] font-semibold uppercase tracking-widest mb-2">HASPC checklist</p>
              <div className="flex flex-wrap gap-2">
                {BEATS.map((b) => {
                  const ok = v.checklist?.[b.key];
                  const isProof = b.key === "proof";
                  return (
                    <span key={b.key} data-testid={`beat-${b.key}-${i}`}
                      className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${
                        ok ? "bg-[#e7f6ec] text-[#15803d] border-[#bfe6cc]" : "bg-[#fef2f2] text-[#dc2626] border-[#fbcfcf]"
                      } ${isProof ? "ring-2 ring-offset-1 " + (ok ? "ring-[#22c55e]" : "ring-[#ef4444]") : ""}`}>
                      {ok ? <CheckCircle size={13} weight="fill" /> : <XCircle size={13} weight="fill" />}
                      {b.label}
                    </span>
                  );
                })}
              </div>
              {v.proof_present ? (
                <div className="mt-2 flex items-center gap-1.5 text-[#15803d] text-xs" data-testid={`proof-ok-${i}`}>
                  <ShieldCheck size={14} weight="fill" /> Proof: {v.proof_points?.join("; ") || "credibility point included"}
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-1.5 text-[#dc2626] text-xs font-semibold" data-testid={`proof-missing-${i}`}>
                  <ShieldWarning size={14} weight="fill" /> Proof beat missing — add a credibility point before approval.
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <Button size="sm" onClick={() => openSend(v)} className="bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white gap-1 rounded-[10px] h-8" data-testid={`send-caption-${i}`}>
                <CalendarPlus size={14} /> Send to Calendar
              </Button>
              <Button size="sm" variant="outline" onClick={() => openRefine(v)} className="border-[#e5e7eb] text-[#3C4048] hover:bg-[#f5f5f5] gap-1 rounded-[10px] h-8" data-testid={`refine-caption-${i}`}>
                <ArrowsClockwise size={14} /> Refine
              </Button>
              <Button size="sm" variant="ghost" onClick={() => copy(v)} className="text-[#3A7A8C] hover:bg-[#f0f7f8] gap-1 rounded-[10px] h-8" data-testid={`copy-caption-${i}`}>
                <Copy size={14} /> Copy
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Refine dialog */}
      <Dialog open={refineOpen} onOpenChange={setRefineOpen}>
        <DialogContent className="bg-white border-[#e5e7eb] max-w-lg">
          <DialogHeader><DialogTitle className="text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Refine caption</DialogTitle></DialogHeader>
          <p className="text-sm text-[#7A9090] -mt-2">What should change? (e.g. "make the hook a number", "stronger proof point", "shorter").</p>
          <Textarea data-testid="refine-instruction" value={refineText} onChange={(e) => setRefineText(e.target.value)} className="border-[#e5e7eb] text-[#3C4048] min-h-[90px]" />
          <div className="flex gap-3">
            <Button data-testid="refine-submit" onClick={doRefine} className="flex-1 bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white rounded-[12px]">Refine</Button>
            <Button onClick={() => setRefineOpen(false)} variant="outline" className="border-[#e5e7eb] text-[#3C4048] rounded-[12px]">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send to calendar dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="bg-white border-[#e5e7eb] max-w-lg">
          <DialogHeader><DialogTitle className="text-[#3C4048]" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>Send to Calendar</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Platform</Label>
              <select value={sendForm.platform} onChange={(e) => setSendForm((f) => ({ ...f, platform: e.target.value }))} className="w-full border border-[#e5e7eb] rounded-[10px] p-2 text-sm text-[#3C4048]" data-testid="send-platform">
                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[#7A9090] text-xs font-semibold uppercase tracking-wider mb-1.5 block">Date</Label>
              <input type="datetime-local" value={sendForm.scheduled_date} onChange={(e) => setSendForm((f) => ({ ...f, scheduled_date: e.target.value }))} className="w-full border border-[#e5e7eb] rounded-[10px] p-2 text-sm text-[#3C4048]" data-testid="send-date" />
            </div>
          </div>
          <Textarea value={sendForm.text} onChange={(e) => setSendForm((f) => ({ ...f, text: e.target.value }))} className="border-[#e5e7eb] text-[#3C4048] min-h-[160px] whitespace-pre-line" data-testid="send-text" />
          <div className="flex gap-3">
            <Button data-testid="send-submit" onClick={doSend} disabled={sending} className="flex-1 bg-[#5FA8B4] hover:bg-[#3A7A8C] text-white rounded-[12px]">{sending ? "Sending…" : "Add to Calendar"}</Button>
            <Button onClick={() => setSendOpen(false)} variant="outline" className="border-[#e5e7eb] text-[#3C4048] rounded-[12px]">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
